import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { observer } from 'mobx-react-lite';
import type { datetime } from '@bringg/types';
import moment, { Moment } from 'moment';
import { osFinder, OSName } from '@bringg/frontend-utils';
import { Notification } from '@bringg/react-components';
import { GanttTimeline } from '@bringg-frontend/gantt-timeline';
import type {
	ExtendedTimelineItem,
	GanttOptions,
	GanttTimeLineItem,
	TimelineItemValuesHistory
} from '@bringg-frontend/gantt-timeline';

import { getDefaultTimeWindow } from '../../utils/date-utils';
import { RoutesPlannerTimelineConfig, ROUTE_PLANNER_CLASS_NAME } from '../routes-planner/routes-planner';
import useGantt from '../routes-planner/hooks/useGantt';
import {
	isTimelineItemInTimeWindowRange,
	getChangedLinkedTasksByTimeError
} from '../routes-planner/timeline-logic/tasks-logic';
import { getResourceDeliveryBlocks } from '../routes-planner/timeline-logic/delivery-block-logic';
import useStore from '../../stores/helpers/use-context';
import TaskTooltipWrapper from '../../components/task-tooltip-body/task-tooltip-wrapper';
import MissingSkillsModal from '../../components/missing-skills-modal/missing-skills-modal';
import { useSkillsValidator } from '../../components/routes-planner/hooks/use-skills-validator';
import { GanttItems, getClusterContent } from '../../utils/bringgTypesTransformers';
import ConsecutiveRouteTooltip, {
	ConsecutiveRouteTooltipProps
} from '../consecutive-route-tooltip/consecutive-route-tooltip';
import { tasksRangeTime } from '../../services/task/tasks-range-time';
import { UpdatingSource } from '../types';

import './routes-planner-timeline.scss';

interface Props {
	onRemoveRow?: (id: number) => void;
	onTitleClick?: (taskId: string) => void;
	configuration: RoutesPlannerTimelineConfig;
	changeTasksSelection: (tasksIds: number[], source: UpdatingSource) => void;
}

export const taskTooltipRender = (
	ganttItems: GanttTimeLineItem[],
	removeTooltip?: () => void,
	setShouldShowTooltipArrow?: (shouldShow: boolean) => void
) => (
	<TaskTooltipWrapper
		taskIds={ganttItems.map(item => Number(item.id))}
		removeTooltip={removeTooltip}
		setShouldShowTooltipArrow={setShouldShowTooltipArrow}
	/>
);

export const consecutiveRouteTooltip = (props: ConsecutiveRouteTooltipProps) => <ConsecutiveRouteTooltip {...props} />;

const fillMissingWindow = (startTime: datetime, endTime: datetime): Moment[] => {
	if (startTime && endTime) {
		return [moment(startTime), moment(endTime)];
	} else if (startTime && !endTime) {
		return [moment(startTime), moment(startTime).endOf('day')];
	} else if (!startTime && endTime) {
		return [moment(endTime).startOf('day'), moment(endTime)];
	} else {
		return getDefaultTimeWindow();
	}
};

const RoutesPlannerTimeline = ({ onRemoveRow, configuration, changeTasksSelection }: Props) => {
	const store = useStore();
	const [shouldShowTooltipArrow, setShouldShowTooltipArrow] = useState(true);
	const runOnce = useRef(false);

	const {
		setGanttRef,
		ganttApi,
		ganttColumns,
		ganttGroups,
		ganttItems,
		adjustItemBeforeMoveEnded,
		centerMapByTask,
		onItemMoveEnded,
		onDrop,
		onDropObjectOnItem,
		showTimeWindowRange,
		removeTimeWindowRange,
		translations,
		onItemsChange,
		showTimeWindowRangeById,
		setSelectedItems
	} = useGantt();

	useEffect(() => {
		if (store.selectedTaskId) {
			//@ts-ignore
			const timelineItem = ganttApi.api.itemsData.get(store.selectedTaskId);
			showTimeWindowRangeById(timelineItem);
		} else {
			removeTimeWindowRange();
		}
	}, [store.selectedTaskId]);

	useEffect(() => {
		if (store.ganttStore.ganttApi?.api && !runOnce.current) {
			const timeline = store.ganttStore.ganttApi.api;
			timeline.on('select', data => {
				changeTasksSelection(data.items, UpdatingSource.Timeline);
				runOnce.current = true;
			});
		}
		return () => store.ganttStore.ganttApi?.api.off('select');
	}, [ganttApi]);

	const { areAllRequiredSkillsOnNewRoute } = useSkillsValidator();
	const [lastWindow, setLastWindow] = useState<{ start: Moment; end: Moment }>();

	const [missingSkills, setMissingSkills] = useState([]);

	const getMinAndMaxAxisTimeOptions = () => {
		const hasConsecutiveRoutes =
			store.shouldCalculateEtaGroupedByDriver && store.consecutiveRoutesStore.routesById.size > 0;
		const consecutiveRoutesTasks = hasConsecutiveRoutes
			? Array.from(store.consecutiveRoutesStore.tasksById.values())
			: [];
		const plannedRoutesTasks = Object.values(store.runToTasksInPresentedDate).flat();

		const tasks = [...plannedRoutesTasks, ...consecutiveRoutesTasks];

		const options: GanttOptions = {};

		if (!tasks || tasks.length === 0) {
			return options;
		}

		const { min: startWindowTime, max: endWindowTime } = tasksRangeTime.getMaxAndMinTasksRange(tasks);

		const [start, end] = fillMissingWindow(startWindowTime, endWindowTime);
		// @ts-ignore
		options.minAxisTime = start.clone().subtract(configuration.axisBoundaryBufferHours, 'hours');
		// @ts-ignore
		options.maxAxisTime = end.clone().add(configuration.axisBoundaryBufferHours, 'hours');

		if (!lastWindow || start.isAfter(lastWindow.end) || end.isBefore(lastWindow.start)) {
			ganttApi.api.setWindow(options.minAxisTime.toISOString(), options.maxAxisTime.toISOString());
			// @ts-ignore
			setLastWindow({ start: options.minAxisTime, end: options.maxAxisTime });
		}

		return options;
	};

	const doubleClickByItemType = (items: GanttTimeLineItem[]) => {
		const itemType = items[0].customData.type;

		switch (itemType) {
			case GanttItems.CONSECUTIVE_ROUTE:
				return;
			default:
				centerMapByTask(items);
		}
	};

	const checkIfNewItemPositionValid = (item: ExtendedTimelineItem) => {
		// @ts-ignore
		const ganttItemStartTime = ganttApi.api.itemsData.get(item.customData.linkedTaskId)?.start;
		const { success, message } = getChangedLinkedTasksByTimeError(
			item,
			store.tasksById.get(+item.id),
			ganttItemStartTime,
			translations.notificationsTranslations
		);
		if (!success) {
			setSelectedItems([+item.id]);
			Notification.error(message);
		}
		return success;
	};

	const options = useMemo(
		() =>
			ganttApi?.api
				? {
						onDropObjectOnItem,
						isOutOfItemRange: (item: ExtendedTimelineItem, currentStart: string, currentEnd: string) =>
							!isTimelineItemInTimeWindowRange(item, currentStart, currentEnd),
						showItemRange: (item: ExtendedTimelineItem) => showTimeWindowRange(item.id),
						hideAllItemsRanges: () => removeTimeWindowRange(),
						isOutOfGroupRange: (groupId: number, currentStart: string, currentEnd: string) =>
							!getResourceDeliveryBlocks(
								store.routesById[groupId],
								store.deliveryBlocksByDriver,
								store.deliveryBlocksByVehicle,
								currentStart,
								currentEnd,
								configuration.canSetTasksPastDeliveryBlock
							).length,
						multiselect: !store.isReadOnly && store.multiSelectedEnabled,
						onOutOfGroupRange: () =>
							Notification.error(translations.notificationsTranslations.outOfDeliveryBlockRange),
						onItemDroppedBeforeCurrentTime: store.fromDispatch
							? () =>
									Notification.error(
										translations.notificationsTranslations.itemDroppedBeforeCurrentTime
									)
							: undefined,
						isNewItemPositionValid: (item: ExtendedTimelineItem) => checkIfNewItemPositionValid(item),
						zoomMax: configuration.zoomMax,
						showCurrentTime: store.fromDispatch,
						...getMinAndMaxAxisTimeOptions()
				  }
				: {},
		[ganttApi, store.runToTasksInPresentedDate]
	);

	const clearSelectedTaskId = () => {
		store.setSelectedTaskId(null);
	};

	const renderTooltip = (gantItems: GanttTimeLineItem[], removeTooltip?: () => void) => {
		if (gantItems[0].customData?.type === GanttItems.CONSECUTIVE_ROUTE) {
			return consecutiveRouteTooltip({ gantItems, store, translations });
		}
		return taskTooltipRender(gantItems, removeTooltip, setShouldShowTooltipArrow);
	};

	const onMissingSkillsModalCancel = () => {
		store.undoLastChange();
		setMissingSkills([]);
	};

	const onMissingSkillsModalApprove = () => {
		setMissingSkills([]);
	};

	const checkForMissingSkills = (item: GanttTimeLineItem, valuesHistory: TimelineItemValuesHistory) => {
		if (item.rowId !== valuesHistory.previousGroupId) {
			const itemTask = store.tasksById.get(+item.id);
			const checkAreAllRequiredSkillsOnNewRoute = areAllRequiredSkillsOnNewRoute([itemTask], +item.rowId);
			if (!checkAreAllRequiredSkillsOnNewRoute.success) {
				setMissingSkills(checkAreAllRequiredSkillsOnNewRoute.missingSkills);
			}
		}
	};

	const beforeItemMoveEnded = useCallback(
		async (item: GanttTimeLineItem, valuesHistory: TimelineItemValuesHistory) => {
			checkForMissingSkills(item, valuesHistory);
			return await onItemMoveEnded(item, valuesHistory);
		},
		[onItemMoveEnded]
	);

	const customEvent = (event, timeline) => {
		const itemFromElement = element => {
			const MAX_TRIES = 10;
			let cur = element;
			let tries = 1;
			while (cur && tries < MAX_TRIES) {
				// eslint-disable-next-line no-prototype-builtins
				if (cur.hasOwnProperty('vis-item')) {
					return cur['vis-item'];
				}
				cur = cur.parentNode;
				tries++;
			}
			return null;
		};

		const item = itemFromElement(event.target);
		const osName: OSName = osFinder.find();
		const isMacWithCtrl = osName === OSName.MacOS && event.srcEvent.ctrlKey;
		const isWindowsWithMeta = osName === OSName.Windows && event.srcEvent.metaKey;
		const isTask = store.tasksById.get(item?.id);
		const specialKey = event.srcEvent.ctrlKey || event.srcEvent.shiftKey || event.srcEvent.metaKey;
		const selectionItems = timeline.getSelection();
		const firstSelectedItem = timeline.itemSet.getItemById(selectionItems[0]);
		const selectedItemsLength = selectionItems.length;
		const sameRoute = item && selectedItemsLength && firstSelectedItem.data.group === item.data.group;
		const tryingSelectItemFromOtherRoute =
			event.type === 'tap' && specialKey && isTask && selectedItemsLength && !sameRoute;

		if (isMacWithCtrl || isWindowsWithMeta || (item && !isTask) || tryingSelectItemFromOtherRoute) {
			if (tryingSelectItemFromOtherRoute) {
				Notification.info(translations.multiselectTranslations.dontSelectFromOtherRoute);
			}
			event.stopPropagation();
			return;
		}
	};

	return (
		<div className="time-line-container">
			<GanttTimeline
				className="routes-planner-timeline"
				ref={setGanttRef}
				items={ganttItems}
				rows={ganttGroups}
				columns={ganttColumns}
				onRemoveRow={onRemoveRow}
				onItemDoubleClicked={doubleClickByItemType}
				onItemMoveEnded={beforeItemMoveEnded}
				adjustItemBeforeMoveEnded={adjustItemBeforeMoveEnded}
				onDrop={onDrop}
				onItemsChange={onItemsChange}
				toolTipRender={renderTooltip}
				options={options}
				inProgressList={store.inProgressEtaCalculationsList}
				clearSelectedItem={clearSelectedTaskId}
				shouldShowTooltipArrow={shouldShowTooltipArrow}
				getClusterContent={getClusterContent}
				tooltipContainerSelector={ROUTE_PLANNER_CLASS_NAME}
				timeZone={store.team.time_zone}
				isMultiselectEnabled={store.multiSelectedEnabled}
				customEvent={customEvent}
				isReadOnly={store.isReadOnly}
			/>
			<MissingSkillsModal
				isVisible={!!missingSkills?.length}
				onOk={onMissingSkillsModalApprove}
				onCancel={onMissingSkillsModalCancel}
				translations={translations.notificationsTranslations}
				missingSkills={missingSkills}
			/>
		</div>
	);
};

export default observer(RoutesPlannerTimeline);
